import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import PaymentsPanel from '../components/PaymentsPanel';

interface Client {
  id: number;
  client_type: string;
  company_name?: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  country?: string;
  city?: string;
  address?: string;
  inn?: string;
  bank_name?: string;
  bank_account?: string;
  bank_code?: string;
  notes?: string;
  is_active: number;
}

const emptyForm = {
  client_type: 'company',
  company_name: '', contact_person: '', phone: '', email: '',
  country: '', city: '', address: '', inn: '',
  bank_name: '', bank_account: '', bank_code: '', notes: '',
};

const Clients: React.FC = () => {
  const { user } = useAuth();
  // (F) qarz/to'lovlar - faqat buxgalteriya va rahbariyat (server ham tekshiradi)
  const canBilling = user?.access_level === 'company' || user?.department === 'accounting';
  const { canEdit } = usePermissions();
  const canEditPerm = canEdit;
  const canFull = canEdit('clients');
  const [clients, setClients] = useState<Client[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState<Client | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = clients.filter(c => {
    const matchSearch =
      (c.company_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.contact_person || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.phone || '').includes(search) ||
      (c.email || '').toLowerCase().includes(search.toLowerCase());
    const matchType = filterType ? c.client_type === filterType : true;
    const matchActive = activeOnly ? c.is_active === 1 : true;
    return matchSearch && matchType && matchActive;
  });

  const openAdd = () => { setForm(emptyForm); setEditId(null); setError(''); setShowModal(true); };
  const openEdit = (c: Client) => {
    setForm({
      client_type: c.client_type,
      company_name: c.company_name || '',
      contact_person: c.contact_person || '',
      phone: c.phone || '',
      email: c.email || '',
      country: c.country || '',
      city: c.city || '',
      address: c.address || '',
      inn: c.inn || '',
      bank_name: c.bank_name || '',
      bank_account: c.bank_account || '',
      bank_code: c.bank_code || '',
      notes: c.notes || '',
    });
    setEditId(c.id); setError(''); setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.company_name && !form.contact_person) {
      setError("Kompaniya nomi yoki kontakt shaxs kiritilishi kerak");
      return;
    }
    setLoading(true); setError('');
    try {
      if (editId) await api.put(`/clients/${editId}`, form);
      else await api.post('/clients', form);
      setShowModal(false); fetchData();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Xatolik yuz berdi');
    } finally { setLoading(false); }
  };

  const handleToggleActive = async (c: Client) => {
    if (!canFull) return;
    await api.put(`/clients/${c.id}`, { is_active: c.is_active === 1 ? 0 : 1 });
    fetchData();
  };

  const handleDelete = async (id: number) => {
    if (!canFull) return;
    if (!window.confirm("O'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/clients/${id}`); fetchData();
  };

  const f = (key: string, val: string) => setForm({ ...form, [key]: val });
  const getDisplayName = (c: Client) => c.client_type === 'company' ? c.company_name : c.contact_person;

  return (
    <div className="p-3 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Mijozlar</h2>
        <button onClick={() => canFull && openAdd()} disabled={!canFull}
          className={`px-3 md:px-4 py-2 rounded-lg text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
          + Qo'shish
        </button>
      </div>

      {/* Stats - 2 col mobile, 4 col desktop */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <p className="text-gray-500 text-xs md:text-sm">Jami</p>
          <p className="text-xl md:text-2xl font-bold text-gray-800">{clients.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <p className="text-gray-500 text-xs md:text-sm">Kompaniyalar</p>
          <p className="text-xl md:text-2xl font-bold text-blue-600">{clients.filter(c => c.client_type === 'company').length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <p className="text-gray-500 text-xs md:text-sm">Jismoniy</p>
          <p className="text-xl md:text-2xl font-bold text-purple-600">{clients.filter(c => c.client_type === 'individual').length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <p className="text-gray-500 text-xs md:text-sm">Faol</p>
          <p className="text-xl md:text-2xl font-bold text-green-600">{clients.filter(c => c.is_active).length}</p>
        </div>
      </div>

      {/* Filtrlar */}
      <div className="flex flex-col md:flex-row gap-2 md:gap-3 mb-4">
        <input type="text" placeholder="Qidirish..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <div className="flex gap-2">
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="flex-1 md:flex-none border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Barcha turlar</option>
            <option value="company">Kompaniya</option>
            <option value="individual">Jismoniy</option>
          </select>
          <label className="flex items-center gap-2 border rounded-lg px-3 py-2 text-sm cursor-pointer whitespace-nowrap">
            <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} />
            Faollar
          </label>
        </div>
      </div>

      {/* Desktop jadval */}
      <div className="hidden md:block bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Mijoz</th>
              <th className="px-4 py-3 text-left">Tur</th>
              <th className="px-4 py-3 text-left">Kontakt</th>
              <th className="px-4 py-3 text-left">Joylashuv</th>
              <th className="px-4 py-3 text-left">INN</th>
              <th className="px-4 py-3 text-center">Holat</th>
              <th className="px-4 py-3 text-center">Amallar</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-gray-400">Mijozlar yo'q</td></tr>
            ) : filtered.map(c => (
              <tr key={c.id} className={`border-t hover:bg-gray-50 ${!c.is_active ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 font-medium">{getDisplayName(c)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.client_type === 'company' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                    {c.client_type === 'company' ? 'Kompaniya' : 'Jismoniy'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {c.contact_person && <div>{c.contact_person}</div>}
                  {c.phone && <div className="text-xs text-blue-500">{c.phone}</div>}
                  {c.email && <div className="text-xs text-gray-400">{c.email}</div>}
                </td>
                <td className="px-4 py-3 text-gray-600">{c.country && c.city ? `${c.country}, ${c.city}` : c.country || c.city || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{c.inn || '—'}</td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => handleToggleActive(c)} disabled={!canFull}
                    className={`px-2 py-1 rounded-full text-xs font-medium ${!canFull ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : c.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}>
                    {c.is_active ? 'Faol' : 'Nofaol'}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => setShowDetail(c)} className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs">👁️</button>
                    <button onClick={() => canFull && openEdit(c)} disabled={!canFull}
                      className={canFull ? "px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>✏️</button>
                    <button onClick={() => handleDelete(c.id)} disabled={!canFull}
                      className={canFull ? "px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile kard ko'rinishi */}
      <div className="md:hidden space-y-3">
        {filtered.length === 0 ? (
          <div className="text-center py-10 text-gray-400 bg-white rounded-xl shadow">Mijozlar yo'q</div>
        ) : filtered.map(c => (
          <div key={c.id} className={`bg-white rounded-xl shadow p-4 ${!c.is_active ? 'opacity-50' : ''}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-bold text-gray-800">{getDisplayName(c)}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${c.client_type === 'company' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                  {c.client_type === 'company' ? 'Kompaniya' : 'Jismoniy'}
                </span>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {c.is_active ? 'Faol' : 'Nofaol'}
              </span>
            </div>
            {c.phone && <p className="text-sm text-blue-500 mb-1">{c.phone}</p>}
            {c.email && <p className="text-xs text-gray-400 mb-1">{c.email}</p>}
            {(c.country || c.city) && <p className="text-xs text-gray-500">{c.country}{c.city ? `, ${c.city}` : ''}</p>}
            <div className="flex gap-2 mt-3 pt-3 border-t">
              <button onClick={() => setShowDetail(c)} className="flex-1 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs">Ko'rish</button>
              {canFull && <button onClick={() => openEdit(c)} className="flex-1 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs">Tahrirlash</button>}
              {canFull && <button onClick={() => handleDelete(c.id)} className="flex-1 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs">O'chirish</button>}
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-xl w-full md:max-w-2xl p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold mb-4">{editId ? 'Tahrirlash' : 'Yangi mijoz'}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="flex gap-2 md:gap-3 mb-4">
              {[{ val: 'company', label: 'Kompaniya' }, { val: 'individual', label: 'Jismoniy shaxs' }].map(t => (
                <button key={t.val} onClick={() => f('client_type', t.val)}
                  className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition ${form.client_type === t.val ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200'}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {form.client_type === 'company' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kompaniya nomi *</label>
                  <input value={form.company_name} onChange={e => f('company_name', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{form.client_type === 'company' ? 'Kontakt shaxs' : 'F.I.O *'}</label>
                <input value={form.contact_person} onChange={e => f('contact_person', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                <input value={form.phone} onChange={e => f('phone', e.target.value)}
                  placeholder="+998 90 123 45 67"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={form.email} onChange={e => f('email', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">INN</label>
                <input value={form.inn} onChange={e => f('inn', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mamlakat</label>
                <input value={form.country} onChange={e => f('country', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Shahar</label>
                <input value={form.city} onChange={e => f('city', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Manzil</label>
                <input value={form.address} onChange={e => f('address', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              {/* Bank rekvizitlari */}
              <div className="md:col-span-2">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">🏦 Bank rekvizitlari</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Bank nomi</label>
                    <input value={form.bank_name} onChange={e => f('bank_name', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Hisob raqami</label>
                    <input value={form.bank_account} onChange={e => f('bank_account', e.target.value)}
                      placeholder="Bir nechta bo'lsa, vergul bilan ajrating"
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Bank kodi (MFO)</label>
                    <input value={form.bank_code} onChange={e => f('bank_code', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Izoh</label>
                <textarea value={form.notes} onChange={e => f('notes', e.target.value)} rows={3}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4 md:mt-6">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor qilish</button>
              <button onClick={handleSubmit} disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center z-50">
          <div className={`bg-white rounded-t-2xl md:rounded-2xl shadow-xl w-full ${canBilling ? 'md:max-w-3xl' : 'md:max-w-md'} p-4 md:p-6 max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">{showDetail.client_type === 'company' ? '🏢' : '👤'}</span>
              <div>
                <h3 className="text-lg md:text-xl font-bold">{getDisplayName(showDetail)}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${showDetail.client_type === 'company' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                  {showDetail.client_type === 'company' ? 'Kompaniya' : 'Jismoniy shaxs'}
                </span>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              {[
                { label: 'Kontakt shaxs', value: showDetail.contact_person },
                { label: 'Telefon', value: showDetail.phone },
                { label: 'Email', value: showDetail.email },
                { label: 'INN', value: showDetail.inn },
                { label: 'Mamlakat', value: showDetail.country },
                { label: 'Shahar', value: showDetail.city },
                { label: 'Manzil', value: showDetail.address },
                { label: 'Bank nomi', value: showDetail.bank_name },
                { label: 'Hisob raqami', value: showDetail.bank_account },
                { label: 'Bank kodi (MFO)', value: showDetail.bank_code },
                { label: 'Izoh', value: showDetail.notes },
              ].map(item => item.value ? (
                <div key={item.label} className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">{item.label}</span>
                  <span className="font-medium text-right">{item.value}</span>
                </div>
              ) : null)}
            </div>
            {canBilling && <PaymentsPanel clientId={showDetail.id} canFull={canEditPerm('accounting')} showInvoices />}
            <div className="flex justify-end mt-4 gap-2">
              {canFull && <button onClick={() => { setShowDetail(null); openEdit(showDetail); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">Tahrirlash</button>}
              <button onClick={() => setShowDetail(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Yopish</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
